import { useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuth } from '@/hooks/useAuth'
import { takeIntendedRoute } from '@/lib/intended-route'

/**
 * Callback landing route (ADR-0011). This is a loading/error SHELL
 * only — the `AuthProvider` is the single owner that processes the Authorization
 * Code + PKCE callback. This component MUST NOT call `signinRedirectCallback()`,
 * hit token/backend session endpoints, or run custom PKCE logic.
 *
 * It reflects `useAuth` state and, ONLY after full authentication (OIDC valid +
 * `/users/auth/me` succeeded), restores the sanitized intended route. Provider/`/users/auth/me`
 * errors render a safe, actionable state without exposing OAuth details.
 */
export function AuthCallbackPage() {
	const { isAuthenticated, isLoading, error } = useAuth()
	const navigate = useNavigate()

	useEffect(() => {
		if (isAuthenticated) {
			void navigate(takeIntendedRoute(), { replace: true })
		}
	}, [isAuthenticated, navigate])

	if (error || (!isLoading && !isAuthenticated)) {
		return (
			<div className="min-h-screen flex items-center justify-center bg-ds-surface">
				<div className="max-w-md w-full text-center space-y-3 p-8">
					<h1 className="text-2xl font-bold text-ds-text">Sign-in could not be completed</h1>
					<p className="text-sm text-ds-text-muted">Please try signing in again.</p>
					<Link to="/login" className="text-ds-sm text-ds-text hover:underline">
						Back to sign in
					</Link>
				</div>
			</div>
		)
	}

	return <LoadingScreen />
}
